/**
 * RecordActionButton Component
 *
 * Renders a single record action button (delete, undelete, purge) with optional
 * confirmation popover. Used by RecordActions container to render applicable
 * actions for a record based on its deleted state and lifecycle state.
 */

import { useState } from 'react'
import { Button, Popconfirm, Tooltip, message } from 'antd'
import * as Icons from '@ant-design/icons'
import { apiService } from '@/services/apiService'
import type { RecordAction } from '@/services/metadataService'

/**
 * Record action key type
 */
export type RecordActionKey = 'delete' | 'undelete' | 'purge'

/**
 * Props for RecordActionButton
 */
export interface RecordActionButtonProps {
  /** The action key ('delete', 'undelete', 'purge') */
  actionKey: RecordActionKey
  /** Action metadata from backend */
  action: RecordAction
  /** The record to perform action on */
  record: Record<string, unknown>
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Entity label for messages (e.g., 'Offering') */
  entityLabel: string
  /** Called after successful action */
  onSuccess?: () => void
}

/**
 * Interpolate placeholders in message templates
 *
 * Supported placeholders:
 * - {entity} - Entity label (e.g., "Offering")
 * - {name} - Record name field if present
 * - {uid} - Record UID
 */
function interpolateMessage(
  template: string,
  entityLabel: string,
  record: Record<string, unknown>
): string {
  return template
    .replace(/\{entity\}/g, entityLabel)
    .replace(/\{name\}/g, String(record.name || record.uid || ''))
    .replace(/\{uid\}/g, String(record.uid || ''))
}

/**
 * Get Ant Design icon component by name
 *
 * Icons are referenced by their export name from @ant-design/icons
 * e.g., 'DeleteOutlined', 'UndoOutlined'
 */
function getIconComponent(iconName?: string): React.ReactNode {
  if (!iconName) return null

  // Type assertion needed because Icons is typed as module
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IconComponent = (Icons as any)[iconName] as React.ComponentType | undefined
  if (!IconComponent) {
    console.warn(`[RecordActionButton] Unknown icon: ${iconName}`)
    return null
  }

  return <IconComponent />
}

/**
 * RecordActionButton Component
 *
 * Renders a button for a single record action (delete/undelete/purge) with:
 * - Icon from Ant Design icons
 * - Optional confirmation popover
 * - API call on confirm
 * - Success/error message display
 *
 * API endpoint mapping:
 * - delete: DELETE /{endpoint}/{uid}
 * - undelete: POST /{endpoint}/{uid}/undelete
 * - purge: DELETE /{endpoint}/{uid}/purge
 */
export function RecordActionButton({
  actionKey,
  action,
  record,
  endpoint,
  entityLabel,
  onSuccess,
}: RecordActionButtonProps) {
  const [loading, setLoading] = useState(false)

  // Handle API call for record action
  const handleAction = async () => {
    const uid = record.uid as string
    if (!uid) {
      message.error('Record has no UID')
      return
    }

    setLoading(true)
    try {
      let response

      // Route to correct endpoint based on action
      if (actionKey === 'delete') {
        response = await apiService.delete(`${endpoint}/${uid}`)
      } else if (actionKey === 'undelete') {
        response = await apiService.post(`${endpoint}/${uid}/undelete`, {})
      } else if (actionKey === 'purge') {
        response = await apiService.delete(`${endpoint}/${uid}/purge`)
      } else {
        message.error(`Unknown action: ${actionKey}`)
        return
      }

      if (response.success) {
        // Show success message with interpolation
        const successMsg = action.successMessage
          ? interpolateMessage(action.successMessage, entityLabel, record)
          : `${entityLabel} ${actionKey}d successfully`
        message.success(successMsg)
        onSuccess?.()
      } else {
        // Error already shown by apiService
        console.error(`[RecordActionButton] ${actionKey} failed:`, response.errors)
      }
    } catch (error) {
      console.error(`[RecordActionButton] ${actionKey} error:`, error)
      message.error(`Failed to ${actionKey} ${entityLabel.toLowerCase()}`)
    } finally {
      setLoading(false)
    }
  }

  // For actions with confirmation message, wrap in Popconfirm
  if (action.confirmationMessage) {
    const confirmMsg = interpolateMessage(action.confirmationMessage, entityLabel, record)

    return (
      <Tooltip title={action.label} destroyOnHidden mouseLeaveDelay={0}>
        <Popconfirm
          title={confirmMsg}
          onConfirm={handleAction}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: action.danger }}
        >
          <Button
            type={action.buttonType || 'default'}
            danger={action.danger}
            icon={getIconComponent(action.icon)}
            loading={loading}
            size="small"
          />
        </Popconfirm>
      </Tooltip>
    )
  }

  // No confirmation - direct action
  return (
    <Tooltip title={action.label} destroyOnHidden mouseLeaveDelay={0}>
      <Button
        type={action.buttonType || 'default'}
        danger={action.danger}
        icon={getIconComponent(action.icon)}
        onClick={handleAction}
        loading={loading}
        size="small"
      />
    </Tooltip>
  )
}
